import { useState } from 'react';
import { Button, Input, Modal } from '@react-apps/design-system';
import type { PerkPreset } from '../../domain/entities/catalog.entity';
import { usePerkPresetMutations } from '../hooks/use-perk-presets';

interface EditPerkPresetModalProps {
  preset: PerkPreset;
  open: boolean;
  onClose: () => void;
}

/**
 * Só chave, nome e descrição — os perks do preset se editam na tela de itens.
 * Renomear não mexe em nada já concedido: quem aplicou o preset levou cópias.
 * A **chave** é a exceção que não é rótulo: é por ela que um cadastro pede este
 * preset, então trocá-la muda o que aquele fluxo encontra.
 */
export function EditPerkPresetModal({
  preset,
  open,
  onClose,
}: EditPerkPresetModalProps) {
  const [key, setKey] = useState(preset.key);
  const [name, setName] = useState(preset.name);
  const [description, setDescription] = useState(preset.description);
  const { update } = usePerkPresetMutations();

  const close = () => {
    update.reset();
    onClose();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    update.mutate(
      {
        presetId: preset.id,
        key: key.trim(),
        name: name.trim(),
        description: description.trim(),
      },
      { onSuccess: close },
    );
  };

  const canSubmit =
    key.trim().length > 0 &&
    name.trim().length > 0 &&
    description.trim().length > 0 &&
    !update.isPending;

  return (
    <Modal
      open={open}
      onClose={close}
      title="Editar preset"
      description="Muda o rótulo do pacote. Os perks já concedidos a partir dele continuam como estão."
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Chave"
          value={key}
          onChange={(e) => setKey(e.target.value)}
          helperText="Como o back referencia este preset. Trocar faz o fluxo que pede a chave antiga não achar mais nada."
          required
        />
        <Input
          label="Nome"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />
        <Input
          label="Descrição"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          required
        />
        {update.error && (
          <p className="text-sm text-red-600">
            {update.error instanceof Error
              ? update.error.message
              : 'Falha ao salvar preset'}
          </p>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={close}>
            Cancelar
          </Button>
          <Button type="submit" disabled={!canSubmit}>
            {update.isPending ? 'Salvando…' : 'Salvar'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
